// Local-first persistence via idb-keyval (IndexedDB). All records stay on the
// device. Video bytes are kept on the filesystem (Capacitor) and referenced by
// URI; here we store metadata, landmark streams, and analysis results.

import { get, set, del, keys } from "idb-keyval";
import type {
  AnalysisResult,
  LandmarkStream,
  Patient,
  Session,
} from "./types";
import type { FlexResult } from "../flex/types";

const K = {
  patient: (id: string) => `patient:${id}`,
  session: (id: string) => `session:${id}`,
  stream: (sessionId: string) => `stream:${sessionId}`,
  analysis: (sessionId: string) => `analysis:${sessionId}`,
  video: (sessionId: string) => `video:${sessionId}`,
  // Flex (fitness/wellness) results — kept as their own key/type, never
  // merged into K.analysis/AnalysisResult (see flex/types.ts header comment).
  flex: (sessionId: string) => `flex:${sessionId}`,
};

export function uuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return "id-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

// ---- Patients ----
export async function savePatient(p: Patient): Promise<void> {
  await set(K.patient(p.id), p);
}
export async function getPatient(id: string): Promise<Patient | undefined> {
  return get(K.patient(id));
}
export async function listPatients(): Promise<Patient[]> {
  return listByPrefix<Patient>("patient:");
}

// ---- Sessions ----
export async function saveSession(s: Session): Promise<void> {
  await set(K.session(s.id), s);
}
export async function getSession(id: string): Promise<Session | undefined> {
  return get(K.session(id));
}
export async function listSessions(patientId?: string): Promise<Session[]> {
  const all = await listByPrefix<Session>("session:");
  const filtered = patientId ? all.filter((s) => s.patientId === patientId) : all;
  return filtered.sort((a, b) => b.recordedAt - a.recordedAt);
}

// ---- Landmark streams (large, local only) ----
export async function saveStream(stream: LandmarkStream): Promise<void> {
  await set(K.stream(stream.sessionId), stream);
}
export async function getStream(
  sessionId: string,
): Promise<LandmarkStream | undefined> {
  return get(K.stream(sessionId));
}

// ---- Analysis results ----
export async function saveAnalysis(a: AnalysisResult): Promise<void> {
  await set(K.analysis(a.sessionId), a);
}
export async function getAnalysis(
  sessionId: string,
): Promise<AnalysisResult | undefined> {
  return get(K.analysis(sessionId));
}

// ---- Video blobs (local only; on native, prefer Capacitor Filesystem) ----
export async function saveVideoBlob(sessionId: string, blob: Blob): Promise<void> {
  await set(K.video(sessionId), blob);
}
export async function getVideoBlob(sessionId: string): Promise<Blob | undefined> {
  return get(K.video(sessionId));
}

// ---- Flex (fitness/wellness) results ----
export async function saveFlexResult(r: FlexResult): Promise<void> {
  await set(K.flex(r.sessionId), r);
}
export async function getFlexResult(sessionId: string): Promise<FlexResult | undefined> {
  return get(K.flex(sessionId));
}

// ---- Delete (one-tap purge for a session) ----
export async function deleteSession(sessionId: string): Promise<void> {
  await Promise.all([
    del(K.session(sessionId)),
    del(K.stream(sessionId)),
    del(K.analysis(sessionId)),
    del(K.video(sessionId)),
    del(K.flex(sessionId)),
  ]);
}

async function listByPrefix<T>(prefix: string): Promise<T[]> {
  const allKeys = (await keys()) as string[];
  const matching = allKeys.filter((k) => typeof k === "string" && k.startsWith(prefix));
  const out: T[] = [];
  for (const k of matching) {
    const v = (await get(k)) as T | undefined;
    if (v) out.push(v);
  }
  return out;
}
